import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Loader2, Trash2, Upload } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { DocsLayout } from '@/components/docs'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ApiError } from '@/services/api/client'
import {
  type PlaygroundState,
  playgroundApi,
  type RunEvent,
  streamRun,
} from '@/services/api/playground'
import { CaseSection } from './CaseSection'
import { DataLakeSection } from './DataLakeSection'
import { DemoDocumentPreview } from './DemoDocumentPreview'
import type { StepView } from './StepStrip'

const MAX_FILE_SIZE = 5 * 1024 * 1024
const SUPPORTED_ACCEPT = '.pdf,.docx,.doc,.txt,.md,.log,.csv,.xlsx,.xls,.xlsm'

function upsertStep(prev: StepView[], evt: Extract<RunEvent, { type: 'step' }>): StepView[] {
  const next = prev.filter(s => s.step !== evt.step)
  next.push({ step: evt.step, status: evt.status, duration_ms: evt.duration_ms })
  return next
}

export default function DemoPage() {
  const qc = useQueryClient()
  const { data: state, isLoading } = useQuery({
    queryKey: ['playground'],
    queryFn: () => playgroundApi.get(),
  })

  const [lakeRunning, setLakeRunning] = useState(false)
  const [lakeSteps, setLakeSteps] = useState<StepView[]>([])
  const [lakeError, setLakeError] = useState<string | null>(null)

  const [caseRunning, setCaseRunning] = useState(false)
  const [caseSteps, setCaseSteps] = useState<StepView[]>([])
  const [caseError, setCaseError] = useState<string | null>(null)
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | undefined>(undefined)

  const running = lakeRunning || caseRunning

  const uploadMutation = useMutation({
    mutationFn: (file: File) => playgroundApi.upload(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playground'] }),
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError
          ? (err.data as { detail?: string })?.detail || err.message
          : 'Upload failed'
      toast.error(msg)
    },
  })

  const clearMutation = useMutation({
    mutationFn: () => playgroundApi.clear(),
    onSuccess: () => {
      setLakeSteps([])
      setLakeError(null)
      setCaseSteps([])
      setCaseError(null)
      setSelectedSchemaId(undefined)
      qc.invalidateQueries({ queryKey: ['playground'] })
    },
  })

  const handleFile = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File exceeds the 5 MB demo limit.')
        return
      }
      uploadMutation.mutate(file)
    },
    [uploadMutation]
  )

  const runLake = async () => {
    setLakeRunning(true)
    setLakeSteps([])
    setLakeError(null)
    try {
      await streamRun('/v1/playground/run-lake', {
        onEvent: e => {
          if (e.type === 'step') setLakeSteps(prev => upsertStep(prev, e))
          else if (e.type === 'error') setLakeError(e.error)
        },
      })
    } catch {
      setLakeError('The run was interrupted.')
    } finally {
      setLakeRunning(false)
      qc.invalidateQueries({ queryKey: ['playground'] })
    }
  }

  const runCase = async () => {
    if (!selectedSchemaId) return
    setCaseRunning(true)
    setCaseSteps([])
    setCaseError(null)
    try {
      await streamRun('/v1/playground/run-case', {
        params: { schema_id: selectedSchemaId },
        onEvent: e => {
          if (e.type === 'step') setCaseSteps(prev => upsertStep(prev, e))
          else if (e.type === 'error') setCaseError(e.error)
        },
      })
    } catch {
      setCaseError('The run was interrupted.')
    } finally {
      setCaseRunning(false)
      qc.invalidateQueries({ queryKey: ['playground'] })
    }
  }

  return (
    <DocsLayout fullWidth>
      <div className="space-y-6">
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-foreground">Extraction playground</h1>
          <p className="text-sm text-muted-foreground">
            Upload one file and compare how it is processed in the <strong>DataLake</strong>{' '}
            (lightweight indexing) versus a <strong>Case</strong> (full LLM extraction into a
            knowledge graph). Nothing here is saved to search or the graph — results are for this
            demo only.
          </p>
        </header>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : state ? (
          <>
            <ActiveFileBar
              state={state}
              onClear={() => clearMutation.mutate()}
              clearing={clearMutation.isPending}
              disabled={running}
            />
            <DataLakeSection
              analysis={state.lake_analysis_result}
              chunks={state.lake_chunks}
              status={state.lake_status}
              error={lakeError}
              running={lakeRunning}
              steps={lakeSteps}
              onRun={runLake}
            />
            <CaseSection
              attempts={state.attempts}
              attemptCount={state.case_attempt_count}
              maxAttempts={state.max_case_attempts}
              running={caseRunning}
              steps={caseSteps}
              error={caseError}
              selectedSchemaId={selectedSchemaId}
              onSelectSchema={setSelectedSchemaId}
              onRun={runCase}
            />
          </>
        ) : (
          <FileDropzone onFile={handleFile} uploading={uploadMutation.isPending} />
        )}
      </div>
    </DocsLayout>
  )
}

function ActiveFileBar({
  state,
  onClear,
  clearing,
  disabled,
}: {
  state: PlaygroundState
  onClear: () => void
  clearing: boolean
  disabled: boolean
}) {
  return (
    <Card className="overflow-hidden border-border p-0">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <FileText className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-foreground">{state.filename}</div>
            <div className="text-xs text-muted-foreground">
              {(state.file_size / 1024).toFixed(0)} KB · {state.file_type || 'file'}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={clearing || disabled}
          className="h-9 md:h-8"
        >
          {clearing ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Precistit
        </Button>
      </div>
      <div className="border-t border-border">
        <DemoDocumentPreview
          key={state.filename}
          file={{
            filename: state.filename,
            fileType: state.file_type,
            fileSize: state.file_size,
          }}
        />
      </div>
    </Card>
  )
}

function FileDropzone({ onFile, uploading }: { onFile: (file: File) => void; uploading: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) onFile(file)
  }

  return (
    <Card
      className={`flex flex-col items-center justify-center gap-3 border-2 border-dashed p-6 text-center transition-colors md:p-12 ${
        dragActive ? 'border-primary bg-accent' : 'border-border'
      }`}
      onDragOver={e => {
        e.preventDefault()
        setDragActive(true)
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
    >
      <Upload className="h-8 w-8 text-muted-foreground" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Drop a file here, or click to browse</p>
        <p className="text-xs text-muted-foreground">
          Up to 5 MB · PDF, DOCX, DOC, TXT, MD, CSV, XLSX, XLS
        </p>
      </div>
      <Button
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="h-9 md:h-8"
      >
        {uploading ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="mr-1.5 h-3.5 w-3.5" />
        )}
        Choose file
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={SUPPORTED_ACCEPT}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) onFile(file)
          e.target.value = ''
        }}
      />
    </Card>
  )
}
